import React from 'react';
import { Button, Card, Stack } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { getConfig, makeResetCache, notify, selectUser, useCacheRecord } from 'gossamer-front-end';
import { Event, LivestreamSession } from 'cb-model';
import { isLiveStreamStartAllowed } from '../helpers/isLiveStreamStartAllowed';
import { EventVideoRecordingView } from './EventVideoRecordingView';
import { RotateCcw, Merge, Airplay } from 'lucide-react';

interface LivestreamProps {
  eventId: number;
  livestreamSessionId: string | undefined;
}

export const Livestream = (props: LivestreamProps) => {
  const livestreamSession = useCacheRecord(LivestreamSession, props.livestreamSessionId, !props.livestreamSessionId);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetCache = makeResetCache(dispatch);

  const loaded = !!props.livestreamSessionId && !!livestreamSession.data;
  const isMainHost = user?.userId === livestreamSession.data?.user_id;
  const liveStreamStarted = livestreamSession.data?.status === 'LIVE' || livestreamSession.data?.status === 'COMPLETED';
  const liveStreamCompleted = livestreamSession.data?.status === 'COMPLETED';

  const buttonContent = !isMainHost ? (
    <>
      <Merge size={14} /> Join
    </>
  ) : livestreamSession.data?.status === 'COMPLETED' ? (
    <>
      <RotateCcw size={14} /> Restart
    </>
  ) : livestreamSession.data?.status === 'LIVE' ? (
    <>
      <Airplay size={14} /> Rejoin
    </>
  ) : (
    <>
      <Airplay size={14} /> Start
    </>
  );

  const isLiveStreamAllowed = isLiveStreamStartAllowed(livestreamSession.data?.start);

  const fetchLivestreamApi = async (method: 'POST' | 'GET', path: string, body?: unknown) => {
    const response = await fetch(`${getConfig('gossamer-be-origin')}/gossamer/main/livestream${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: body ? JSON.stringify(body) : undefined,
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data?.error || data?.message || 'Livestream request failed');
    }
    return data;
  };

  async function handleStartLivestream() {
    let livestreamSessionId = props.livestreamSessionId;
    try {
      const notifications = [];
      if (isMainHost && !livestreamSessionId) {
        const resp1 = await fetchLivestreamApi('POST', '/attach', {
          eventId: props.eventId,
          startTime: new Date().toISOString(),
        });
        livestreamSessionId = resp1.id;
        resetCache(Event.id, String(props.eventId));
        notifications.push('started livestream');
      }

      if (isMainHost && !liveStreamStarted) {
        await fetchLivestreamApi('POST', '/sendStartEmails', {
          roomId: livestreamSessionId,
        });
        notifications.push('sent notification emails');
      }

      if (notifications.length > 0) {
        notify(['Successfully ' + notifications.join(' and '), 'success']);
      }
      // I think this needs to be a reload as transitioning from admin to website...
      // navigate(`/host/${livestreamSessionId}`);
      window.location.href = `/host/${livestreamSessionId}`;
    } catch (e) {
      console.error(e);
      notify([e.message || 'An error occurred', 'danger']);
    }
  }

  async function handleTestLivestream() {
    let livestreamSessionId = props.livestreamSessionId;
    try {
      navigate(`/host/${livestreamSessionId}/preview`);
    } catch (e) {
      console.error(e);
      notify([e.message || 'An error occurred', 'danger']);
    }
  }

  const startLivestreamButton = () => (
    <div>
      <Button
        disabled={!isLiveStreamAllowed}
        variant={isLiveStreamAllowed ? 'primary' : 'secondary'}
        size="sm"
        onClick={handleStartLivestream}
        className="d-flex justify-content-start  align-items-center gap-1"
      >
        {buttonContent} livestream
      </Button>
    </div>
  );

  const testLivestreamButton = () => (
    <div>
      <Button variant="success" size="sm" onClick={handleTestLivestream}>
        Test livestream
      </Button>
    </div>
  );

  return (
    <>
      <Card className="border-0">
        <Card.Body className="p-0">
          <Card.Text className="text-muted mb-3">Runs the Careerbook livestream experience, and recordings.</Card.Text>
          {(loaded || !props.livestreamSessionId) && !isLiveStreamAllowed && (
            <div>
              <p className="small">Live streaming will be available 30 minutes before the event start time.</p>
              <p className="small">Use the Test action below to test the livestream functionality.</p>
            </div>
          )}
          {/* Actions */}
          <Stack direction="horizontal" gap={2} className="flex-wrap">
            <Stack direction="horizontal" gap={2} className="flex flex-wrap">
              {(loaded || !props.livestreamSessionId) && startLivestreamButton()}
              {(loaded || !props.livestreamSessionId) && !isLiveStreamAllowed && testLivestreamButton()}
            </Stack>

            {loaded && liveStreamCompleted && (
              <EventVideoRecordingView livestreamSessionId={props.livestreamSessionId} />
            )}
          </Stack>
        </Card.Body>
      </Card>
    </>
  );
};
